import { create } from 'zustand';
import type { KnownIssue } from './supportStore';

export interface AdminSummary {
  users: number;
  projects: { live: number; trash: number };
  runningGames: number;
  storageBytes: { projects: number; restorePoints: number };
}

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  role: string;
  plan: string;
  emailVerified: boolean;
  createdAt: number;
  status: 'active' | 'suspended' | 'deletion-pending';
  suspendedReason: string | null;
  projectsTrashed: number;
  usage: {
    projects: { used: number; limit: number };
    storageBytes: { used: number; limit: number; restorePoints: number };
    runningGames: { used: number; limit: number };
  };
}

export interface AdminProject {
  id: string;
  slug: string;
  name: string;
  ownerUsername: string | null;
  status: string;
  createdAt: number;
  deletedAt: number | null;
  storageBytes: number;
  restorePoints: { count: number; bytes: number };
}

export interface AdminInvite {
  email: string;
  status: 'open' | 'used' | 'expired' | 'revoked';
  createdAt: number;
  expiresAt: number;
  redeemedByUsername: string | null;
}

export interface AdminReport {
  id: string;
  username: string;
  email: string;
  kind: 'bug' | 'idea';
  message: string;
  app: 'dashboard' | 'editor';
  page: string;
  userAgent: string;
  viewport: string;
  projectName: string | null;
  editorTab: string | null;
  createdAt: number;
  closedAt: number | null;
}

interface AdminState {
  summary: AdminSummary | null;
  users: AdminUser[];
  projects: AdminProject[];
  invites: AdminInvite[];
  notAdmin: boolean;
  error: string | null;
  lastInvite: { email: string; code: string; mailed: boolean } | null;

  fetchAll: () => Promise<void>;
  suspend: (userId: string, reason: string) => Promise<void>;
  unsuspend: (userId: string) => Promise<void>;
  setPlan: (userId: string, plan: string) => Promise<void>;
  sendInvite: (email: string) => Promise<void>;
  revokeInvite: (email: string) => Promise<void>;

  reports: AdminReport[];
  showClosedReports: boolean;
  knownIssues: KnownIssue[];
  loadReports: (includeClosed: boolean) => Promise<void>;
  setReportClosed: (id: string, closed: boolean) => Promise<void>;
  loadKnownIssues: () => Promise<void>;
  saveKnownIssue: (input: {
    id?: string;
    title?: string;
    body?: string;
    status?: 'open' | 'fixed';
  }) => Promise<void>;
  deleteKnownIssue: (id: string) => Promise<void>;
}

const API = '/api/platform/admin';

function headers(): HeadersInit {
  const token = localStorage.getItem('platform_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function post(path: string, body?: unknown): Promise<unknown> {
  return send('POST', path, body);
}

async function send(method: string, path: string, body?: unknown): Promise<unknown> {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: headers(),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Request failed');
  return data;
}

class NotAdminError extends Error {}

async function load<T>(path: string): Promise<T> {
  const res = await fetch(`${API}${path}`, { headers: headers() });
  if (res.status === 404) throw new NotAdminError();
  if (!res.ok) throw new Error('Failed to load the admin page');
  return (await res.json()) as T;
}

export const useAdminStore = create<AdminState>((set, get) => ({
  summary: null,
  users: [],
  projects: [],
  invites: [],
  notAdmin: false,
  error: null,
  lastInvite: null,

  fetchAll: async () => {
    try {
      const [summary, users, projects, invites] = await Promise.all([
        load<AdminSummary>('/summary'),
        load<AdminUser[]>('/users'),
        load<AdminProject[]>('/projects'),
        load<AdminInvite[]>('/invites'),
      ]);
      set({ summary, users, projects, invites, error: null });
    } catch (err) {
      if (err instanceof NotAdminError) set({ notAdmin: true });
      else set({ error: err instanceof Error ? err.message : 'Failed to load the admin page' });
    }
  },

  suspend: async (userId, reason) => {
    await post(`/users/${userId}/suspend`, { reason });
    await get().fetchAll();
  },

  unsuspend: async (userId) => {
    await post(`/users/${userId}/unsuspend`);
    await get().fetchAll();
  },

  setPlan: async (userId, plan) => {
    await post(`/users/${userId}/plan`, { plan });
    await get().fetchAll();
  },

  sendInvite: async (email) => {
    const result = (await post('/invites', { email })) as {
      email: string;
      code: string;
      mailed: boolean;
    };
    set({ lastInvite: { email: result.email, code: result.code, mailed: result.mailed } });
    await get().fetchAll();
  },

  revokeInvite: async (email) => {
    await post('/invites/revoke', { email });
    await get().fetchAll();
  },

  reports: [],
  showClosedReports: false,
  knownIssues: [],

  loadReports: async (includeClosed) => {
    try {
      const data = await load<{ reports: AdminReport[] }>(
        `/feedback${includeClosed ? '?closed=1' : ''}`
      );
      set({ reports: data.reports, showClosedReports: includeClosed });
    } catch (err) {
      if (err instanceof NotAdminError) set({ notAdmin: true });
    }
  },

  setReportClosed: async (id, closed) => {
    await post(`/feedback/${id}/${closed ? 'close' : 'reopen'}`);
    await get().loadReports(get().showClosedReports);
  },

  loadKnownIssues: async () => {
    try {
      const data = await load<{ issues: KnownIssue[] }>('/known-issues');
      set({ knownIssues: data.issues });
    } catch (err) {
      if (err instanceof NotAdminError) set({ notAdmin: true });
    }
  },

  saveKnownIssue: async ({ id, ...fields }) => {
    if (id) await send('PATCH', `/known-issues/${id}`, fields);
    else await post('/known-issues', fields);
    await get().loadKnownIssues();
  },

  deleteKnownIssue: async (id) => {
    await send('DELETE', `/known-issues/${id}`);
    await get().loadKnownIssues();
  },
}));
